import { TextFieldProps } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { useCombobox } from 'downshift';
import React, { useState } from 'react';
import { autoComplete } from '../../services/api/Search';
import { ISuggestion, ISuggestionSource, ISuggestionSourceType, ISuggestionSourceUser } from '../../services/types';
import SuggestionItem from './SuggestionItem';

interface IProps<R> {
  render: (props: R) => JSX.Element;
  onSelect?: (item: ISuggestionSource) => void;
  resetFormOnSelect?: boolean;
  type?: ISuggestionSourceType;
  filter?: (s: ISuggestion) => boolean;
  allowDuplicate?: boolean;
  fetchFactory?: (keyword: string, type?: ISuggestionSourceType) => Promise<ISuggestion[] | undefined>;
}

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    flexGrow: 1,
    position: 'relative',
  },
  paper: {
    position: 'absolute',
    zIndex: theme.zIndex.drawer + 2,
    marginTop: theme.spacing(1),
    left: 0,
    right: 0,
    maxHeight: '174px',
    overflowY: 'scroll',
  },
  chip: {
    margin: theme.spacing(0.5, 0.25),
  },
}));

const AutoComplete = <R extends TextFieldProps>(props: IProps<R>): JSX.Element => {
  const classes = useStyles();

  const [suggestions, setSuggestions] = useState<ISuggestion[]>([]);
  const [searchKeyword, setSearchKeyword] = useState<string>('');

  const { render, onSelect, resetFormOnSelect, type, filter, allowDuplicate = false, fetchFactory } = props;

  const { isOpen, selectedItem, getMenuProps, getInputProps, getComboboxProps, highlightedIndex, getItemProps } = useCombobox<ISuggestion>({
    items: suggestions,
    onSelectedItemChange: ({ type, selectedItem }) => {
      if (!selectedItem) {
        return;
      }

      if (type === useCombobox.stateChangeTypes.InputKeyDownEnter) {
        handleSelect(selectedItem);
      }
    },
    onInputValueChange: ({ inputValue }) => {
      fetchAutoComplete(type, inputValue, filter);
    },
  });

  const { onBlur, onFocus, ref: _, ...restInputProps } = getInputProps(
    {
      value: searchKeyword,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        const keyword = String(e.target.value);
        setSearchKeyword(keyword);
      },
    },
    { suppressRefError: true },
  );

  const clearSuggestion = () => setSuggestions([]);

  async function fetchAutoComplete(type?: ISuggestionSourceType, keyword?: string, filterFunction = (s: ISuggestion) => true): Promise<void> {
    if (type === 'writer') {
      fetchAutoComplete('user', keyword, s => filterFunction(s) && (s._source as ISuggestionSourceUser).is_writer);
      return;
    }

    if (keyword && keyword.length >= 3) {
      const data = await fetchData(keyword, type);

      if (data) {
        setSuggestions(data.filter(filterFunction));
      }
    } else {
      clearSuggestion();
    }
  }

  async function fetchData(keyword: string, type?: ISuggestionSourceType): Promise<ISuggestion[] | undefined> {
    if (fetchFactory) {
      const data = await fetchFactory(keyword, type);
      return data;
    }

    const result = await autoComplete(keyword, type, allowDuplicate).catch(clearSuggestion);

    if (result && result.data) {
      return result.data;
    }
  }

  const handleSelect = (suggestion: ISuggestion) => {
    const { _source, text } = suggestion;

    onSelect && onSelect(_source);
    clearSuggestion();

    setSearchKeyword(resetFormOnSelect ? '' : text);
  };

  return (
    <div>
      <div className={classes.container} {...getComboboxProps()}>
        {render({
          InputProps: { onBlur, onFocus },
          onCancel: () => {
            setSearchKeyword('');
            clearSuggestion();
          },
          ...restInputProps,
        } as R)}
        <div {...getMenuProps()}>
          {isOpen && (
            <Paper className={classes.paper} square>
              {suggestions.map((suggestion, index) => (
                <SuggestionItem
                  key={index}
                  suggestion={suggestion}
                  index={index}
                  innerHTML={
                    type === 'writer'
                      ? (suggestion._source as ISuggestionSourceUser).writer_name[0].replace(searchKeyword.toLowerCase(), `<b> ${searchKeyword}</b>`)
                      : undefined
                  }
                  itemProps={getItemProps({ item: suggestion })}
                  highlightedIndex={highlightedIndex}
                  selectedItem={selectedItem}
                  onClick={handleSelect}
                />
              ))}
            </Paper>
          )}
        </div>
      </div>
    </div>
  );
};

export default AutoComplete;
